import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getDatabase, ref, remove, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { 
    connectDomainToSpace, 
    disconnectDomainFromSpace, 
    getSpaceDomain,
    validateDomain,
    connectDomainToSpaceWithProxy,
    disconnectDomainFromSpaceWithProxy,
    generateProxyDNSInstructions,
    testCloudflareWorker
} from '../utils/domainHelpers';
import './SpaceOverviewCard.css';
import AIPageCreator from './AIPageCreator';
import PagesDisplay from './PagesDisplay';

/**
 * Space Overview Card - Moderne, elegante Space-Verwaltung
 */
const SpaceOverviewCard = ({ userUsername, pages, onAddPage, onSpaceDeleted, spaceId, spaceName, onBackToSpaces, activePageIndex: propActivePageIndex, setActivePageIndex: propSetActivePageIndex }) => {
    const [expandedPages, setExpandedPages] = useState(false);
    
    // Verwende Props wenn verfügbar, sonst lokalen State
    const activePageIndex = propActivePageIndex !== undefined ? propActivePageIndex : 0;
    const setActivePageIndex = propSetActivePageIndex || (() => {});
    
    // Debug logging
    useEffect(() => {
        console.log('🔍 SpaceOverviewCard: activePageIndex updated to:', activePageIndex, 'for pages:', pages?.length);
    }, [activePageIndex, pages]);

    // Animation Variants entfernt
    
    // Domain-Management States
    const [domainInfo, setDomainInfo] = useState(null);
    const [showDomainModal, setShowDomainModal] = useState(false);
    const [newDomain, setNewDomain] = useState('');
    const [connectingDomain, setConnectingDomain] = useState(false);
    const [domainError, setDomainError] = useState('');
    const [useCloudflareProxy, setUseCloudflareProxy] = useState(true);
    
    // Animationen entfernt
    const [showDNSInstructions, setShowDNSInstructions] = useState(false);
    const [workerStatus, setWorkerStatus] = useState(null);
    const [showAIPageCreator, setShowAIPageCreator] = useState(false);
    const [showActionsMenu, setShowActionsMenu] = useState(false);

    const db = getDatabase();
    const auth = getAuth();
    const navigate = useNavigate();
    const currentUser = auth.currentUser;

    // Load domain info for this space
    useEffect(() => {
        loadSpaceDomain();
        checkCloudflareWorkerStatus();
    }, [spaceId, currentUser]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        const isModalOpen = showDomainModal || showDNSInstructions;
        
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [showDomainModal, showDNSInstructions]);

    // Close actions menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showActionsMenu && !event.target.closest('.actions-menu-container')) {
                setShowActionsMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showActionsMenu]);



    const loadSpaceDomain = async () => {
        if (!currentUser || !spaceId) return;
        
        try {
            const domain = await getSpaceDomain(currentUser.uid, spaceId);
            setDomainInfo(domain);
            console.log('✅ Space domain loaded:', domain);
        } catch (error) {
            console.error('Error loading space domain:', error);
        }
    };

    // Debug pages count
    useEffect(() => {
        console.log('🔍 SpaceOverviewCard Debug:', {
            userUsername,
            spaceName,
            spaceId,
            pagesLength: pages?.length || 0,
            pages: pages
        });
    }, [pages, userUsername, spaceName, spaceId]);

    const checkCloudflareWorkerStatus = async () => {
        try {
            const status = await testCloudflareWorker();
            setWorkerStatus(status);
            console.log('🧪 Cloudflare Worker Status:', status);
        } catch (error) {
            console.warn('⚠️ Could not check Cloudflare Worker status:', error);
            setWorkerStatus({ success: false, error: error.message });
        }
    };


    const handleConnectDomain = async () => {
        if (!newDomain.trim() || !currentUser || !spaceId) return;
        
        setConnectingDomain(true);
        setDomainError('');
        
        try {
            const validation = validateDomain(newDomain);
            if (!validation.valid) {
                setDomainError(validation.error);
                setConnectingDomain(false);
                return;
            }

            let result;
            
            if (useCloudflareProxy && workerStatus?.success) {
                console.log('🚀 Connecting domain with Cloudflare proxy...');
                result = await connectDomainToSpaceWithProxy(
                    currentUser.uid, 
                    spaceId, 
                    validation.domain, 
                    spaceName || userUsername, 
                    userUsername
                );
            } else {
                console.log('⚡ Connecting domain with Firebase hosting...');
                result = await connectDomainToSpace(
                    currentUser.uid, 
                    spaceId, 
                    validation.domain, 
                    spaceName || userUsername, 
                    userUsername
                );
            }
            
            await loadSpaceDomain();
            
            if (result.proxyEnabled) {
                setShowDNSInstructions(true);
            }
            
            setNewDomain('');
            setShowDomainModal(false);
            setDomainError('');
            
            console.log('✅ Domain connected successfully:', {
                domain: validation.domain,
                proxyEnabled: result.proxyEnabled
            });
            
        } catch (error) {
            console.error('❌ Error connecting domain:', error);
            setDomainError(error.message || 'Failed to connect domain');
        } finally {
            setConnectingDomain(false);
        }
    };

    const handleDisconnectDomain = async () => {
        if (!domainInfo?.domain || !currentUser || !spaceId) return;
        
        const proxyText = domainInfo.mapping?.proxyEnabled ? ' (Unlimited Proxy)' : '';
        const confirmDisconnect = window.confirm(
            `Disconnect ${domainInfo.domain}${proxyText} from this space?`
        );
        
        if (!confirmDisconnect) return;
        
        try {
            if (domainInfo.mapping?.proxyEnabled) {
                await disconnectDomainFromSpaceWithProxy(currentUser.uid, spaceId, domainInfo.domain);
            } else {
                await disconnectDomainFromSpace(currentUser.uid, spaceId, domainInfo.domain);
            }
            
            await loadSpaceDomain();
            setShowDNSInstructions(false);
            
            console.log('✅ Domain disconnected successfully');
        } catch (error) {
            console.error('❌ Error disconnecting domain:', error);
            alert('Failed to disconnect domain. Please try again.');
        }
    };

    const handlePageEdit = (page) => {
        if (spaceId) {
            // For 3-level structure, use pageName (internal key) not slug
            const pageKey = page.pageName || (page.isHomePage ? 'home' : page.slug);
            console.log('🔍 SpaceOverviewCard: Navigating to page edit:', { 
                pageKey, 
                pageName: page.pageName, 
                slug: page.slug, 
                isHomePage: page.isHomePage 
            });
            navigate(`/settings/page/${currentUser.uid}/${spaceId}/${pageKey}`);
        } else {
            const pageId = `${currentUser.uid}/${page.slug || page.customRoute}`;
            navigate(`/settings/page/${pageId}`);
        }
    };

    const getPageUrl = (page) => {
        if (page.isHomePage || page.slug === "/") {
            return `${window.location.origin}/${userUsername}/`;
        } else {
            const slug = page.slug || page.customRoute || page.pageName;
            return `${window.location.origin}/${userUsername}/${slug}`;
        }
    };

    const getDomainUrl = (page) => {
        if (!domainInfo?.domain) return null;
        
        if (page.isHomePage || page.slug === "/") {
            return `https://${domainInfo.domain}/`;
        } else {
            const slug = page.slug || page.customRoute || page.pageName;
            return `https://${domainInfo.domain}/${slug}`;
        }
    };

    return (
        <div className="space-workspace-modern">
            {/* Clean White Header */}
            <div className="space-header-clean">
                <div className="header-top-row">
                    <div className="title-section-clean">
                        {onBackToSpaces && (
                            <button 
                                className="back-btn-clean"
                                onClick={onBackToSpaces}
                                title="Back to Spaces"
                            >
                                <i className="fas fa-arrow-left"></i>
                            </button>
                        )}
                        <div className="title-content">
                            <div className="title-row">
                                <h1 className="space-title-clean">{spaceName || userUsername}</h1>
                                <span className="pages-count-display">{pages?.length || 0} Pages</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="header-actions-clean">
                        {/* 3-Dots Menu for regular actions */}
                        <div className="actions-menu-container">
                            <button 
                                className="header-btn menu-toggle"
                                onClick={() => setShowActionsMenu(!showActionsMenu)}
                                title="More actions"
                            >
                                <i className="fas fa-ellipsis-h"></i>
                            </button>
                            
                            {showActionsMenu && (
                                <div className="actions-dropdown">
                                    <button 
                                        className="dropdown-action"
                                        onClick={() => {
                                            onAddPage();
                                            setShowActionsMenu(false);
                                        }}
                                    >
                                        <i className="fas fa-plus"></i>
                                        <span>{pages?.length === 0 ? 'Create Home Page' : 'Add Page'}</span>
                                    </button>
                                    
                                    <button 
                                        className="dropdown-action"
                                        onClick={() => {
                                            const url = domainInfo?.domain 
                                                ? `https://${domainInfo.domain}/`
                                                : `${window.location.origin}/${userUsername}/`;
                                            navigator.clipboard.writeText(url);
                                            setShowActionsMenu(false);
                                        }}
                                    >
                                        <i className="fas fa-copy"></i>
                                        <span>Copy Link</span>
                                    </button>
                                    
                                    <button 
                                        className="dropdown-action"
                                        onClick={() => {
                                            console.log('🎨 Navigating to Space Settings:', spaceId);
                                            navigate(`/space-settings/${spaceId}`);
                                            setShowActionsMenu(false);
                                        }}
                                    >
                                        <i className="fas fa-cog"></i>
                                        <span>Space Settings</span>
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        {/* AI Page Creator Button - prominent on the right */}
                        <button 
                            className="header-btn ai-creator"
                            onClick={() => {
                                console.log('🤖 Opening AI Page Creator');
                                setShowAIPageCreator(true);
                                document.body.style.overflow = 'hidden';
                            }}
                            title="Create page with AI"
                        >
                            <i className="fas fa-magic"></i>
                            Create page with AI
                        </button>
                        
                    </div>
                </div>
                
                
                {/* Pages Tab Navigation - Integriert in Header */}
                {pages && pages.length > 0 && (
                    <div className="pages-tab-navigation">
                        <div className="pages-tabs-wrapper">
                            {[...pages].reverse().map((page, reversedIndex) => {
                                // Convert reversed index to original index
                                const originalIndex = pages.length - 1 - reversedIndex;
                                return (
                                    <button
                                        key={page.id || `page-${reversedIndex}`}
                                        className={`page-tab ${originalIndex === activePageIndex ? 'active' : ''}`}
                                        onClick={() => {
                                            console.log('🔍 Tab clicked:', { 
                                                reversedIndex, 
                                                originalIndex,
                                                pageName: page.name, 
                                                currentActivePageIndex: activePageIndex,
                                                newActivePageIndex: originalIndex 
                                            });
                                            setActivePageIndex(originalIndex);
                                        }}
                                    >
                                        <span className="page-tab-name">{page.name}</span>
                                        {page.isOnlyForMembers && (
                                            <div className="page-access-indicator" title="Members Only Page">
                                                <i className="fas fa-lock"></i>
                                            </div>
                                        )}
                                        {page.viewCount > 0 && (
                                            <div className="page-tab-views">
                                                <i className="fas fa-eye"></i>
                                                <span>{page.viewCount}</span>
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>


            {/* Domain Connection Modal */}
            {showDomainModal && createPortal(
                <div className="modal-overlay-modern">
                    <div className="modal-content-modern domain-modal">
                        <div className="modal-header-modern primary">
                            <div className="modal-icon">
                                <i className="fas fa-globe"></i>
                            </div>
                            <h3>Connect Custom Domain</h3>
                            <button 
                                className="modal-close-btn"
                                onClick={() => {
                                    setShowDomainModal(false);
                                    setNewDomain('');
                                    setDomainError('');
                                }}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="modal-body-modern">
                            <p>Connect your own domain for professional branding</p>
                            
                            <div className="form-group-modern">
                                <label>Domain Name</label>
                                <input
                                    type="text"
                                    value={newDomain}
                                    onChange={(e) => setNewDomain(e.target.value)}
                                    placeholder="www.yoursite.com"
                                    className="input-modern"
                                    disabled={connectingDomain}
                                />
                                <small>Enter without http:// or https://</small>
                            </div>

                            <div className="proxy-toggle-modern">
                                <label className="toggle-label-modern">
                                    <input
                                        type="checkbox"
                                        checked={useCloudflareProxy}
                                        onChange={(e) => setUseCloudflareProxy(e.target.checked)}
                                        disabled={connectingDomain || !workerStatus?.success}
                                        className="toggle-input-modern"
                                    />
                                    <span className="toggle-slider-modern"></span>
                                    <span className="toggle-text-modern">
                                        Unlimited Proxy System
                                        {workerStatus?.success ? (
                                            <span className="status-available">Available</span>
                                        ) : (
                                            <span className="status-unavailable">Unavailable</span>
                                        )}
                                    </span>
                                </label>
                            </div>

                            {!useCloudflareProxy && (
                                <div className="firebase-warning-modern">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Firebase Hosting: Limited to 36 domains per project</span>
                                </div>
                            )}

                            {domainError && (
                                <div className="error-box-modern">
                                    <i className="fas fa-exclamation-circle"></i>
                                    <span>{domainError}</span>
                                </div>
                            )}
                        </div>
                        
                        <div className="modal-actions-modern">
                            <button 
                                className="btn-modern secondary"
                                onClick={() => {
                                    setShowDomainModal(false);
                                    setNewDomain('');
                                    setDomainError('');
                                }}
                                disabled={connectingDomain}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-modern primary"
                                onClick={handleConnectDomain}
                                disabled={!newDomain.trim() || connectingDomain}
                            >
                                <i className="fas fa-link"></i>
                                {connectingDomain ? 'Connecting...' : 'Connect Domain'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* DNS Instructions Modal */}
            {showDNSInstructions && domainInfo && createPortal(
                <div className="modal-overlay-modern">
                    <div className="modal-content-modern dns-modal">
                        <div className="modal-header-modern info">
                            <div className="modal-icon">
                                <i className="fas fa-cogs"></i>
                            </div>
                            <h3>DNS Setup</h3>
                            <button 
                                className="modal-close-btn"
                                onClick={() => setShowDNSInstructions(false)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="modal-body-modern">
                            <p>Add this DNS record at your domain provider to activate <strong>{domainInfo.domain}</strong>:</p>
                            
                            <div className="dns-record-modern">
                                <div className="dns-record-header">
                                    {domainInfo.mapping?.proxyEnabled ? (
                                        <>
                                            <i className="fas fa-rocket"></i>
                                            <span>Unlimited Proxy System</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-server"></i>
                                            <span>Firebase Hosting</span>
                                        </>
                                    )}
                                </div>
                                
                                <div className="dns-fields">
                                    <div className="dns-field">
                                        <label>Type</label>
                                        <code>CNAME</code>
                                    </div>
                                    <div className="dns-field">
                                        <label>Name</label>
                                        <code>{domainInfo.domain.startsWith('www.') ? 'www' : '@'}</code>
                                    </div>
                                    <div className="dns-field">
                                        <label>Value</label>
                                        <div className="dns-value">
                                            <code>
                                                {domainInfo.mapping?.proxyEnabled 
                                                    ? 'bonusclub-proxy.bonusclub.workers.dev'
                                                    : 'bonusclub-dev.web.app'
                                                }
                                            </code>
                                            <button 
                                                className="copy-dns-btn-modern"
                                                onClick={() => {
                                                    const value = domainInfo.mapping?.proxyEnabled 
                                                        ? 'bonusclub-proxy.bonusclub.workers.dev'
                                                        : 'bonusclub-dev.web.app';
                                                    navigator.clipboard.writeText(value);
                                                }}
                                            >
                                                <i className="fas fa-copy"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="dns-field">
                                        <label>TTL</label>
                                        <code>300</code>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="setup-steps-modern">
                                <h4>Setup Steps</h4>
                                <ol>
                                    <li>Go to your domain provider (GoDaddy, Namecheap, etc.)</li>
                                    <li>Find DNS settings or Domain management</li>
                                    <li>Add the CNAME record above</li>
                                    <li>Wait 2-5 minutes for DNS propagation</li>
                                    <li>Your domain will be live! 🎉</li>
                                </ol>
                            </div>
                            
                            <div className="test-domain-modern">
                                <a 
                                    href={`https://${domainInfo.domain}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="test-link-modern"
                                >
                                    <i className="fas fa-external-link-alt"></i>
                                    Test https://{domainInfo.domain}
                                </a>
                            </div>
                        </div>
                        
                        <div className="modal-actions-modern">
                            <button 
                                className="btn-modern primary"
                                onClick={() => setShowDNSInstructions(false)}
                            >
                                <i className="fas fa-check"></i>
                                Got it!
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* AI Page Creator Modal */}
            {showAIPageCreator && createPortal(
                <AIPageCreator
                    onClose={() => {
                        setShowAIPageCreator(false);
                        document.body.style.overflow = 'auto';
                    }}
                    spaceId={spaceId}
                    spaceName={spaceName}
                    username={userUsername}
                />,
                document.body
            )}
        </div>
    );
};

export default SpaceOverviewCard;